import { infiniteQueryFetchNextPage } from '@repo/sdk'
import type { OrganizationPerson } from '@repo/sdk/gen'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '@repo/ui/empty'
import { getErrorCode } from '@repo/ui/utils'
import {
	useInfiniteQuery,
	useQuery,
	useQueryClient
} from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Hourglass, Shield } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { showsFirstAdminInvitation } from '@/routes/(private)/organizations/-lib/organization-status'
import { AddOwnerAction } from '@/routes/(private)/organizations/$organizationId/-components/add-owner-action'
import { OrganizationOwnersTable } from '@/routes/(private)/organizations/$organizationId/-components/organization-owners-table'
import { UndeliveredInvitationsAlert } from '@/routes/(private)/organizations/$organizationId/-components/undelivered-invitations-alert'
import {
	firstAdminInvitationQueryOptions,
	organizationDetailQueryOptions,
	organizationPeopleQueryOptions
} from '@/routes/(private)/organizations/$organizationId/-query/query-options'

export const Route = createFileRoute(
	'/(private)/organizations/$organizationId/people'
)({
	component: OrganizationPeoplePage
	/*
	 * No `breadcrumb`, deliberately. The trail stops at the customer, and the
	 * tabs below it say which of their two readings is open. A third step
	 * here would make moving between the tabs look like moving to another
	 * page, which it is not: it is the same customer, read a second way.
	 */
})

const PAGE_SIZE = 50

/**
 * Owners, and only owners. The filter is the whole point — see below.
 *
 * `owner` is the string `'true'` and not the boolean: the parameter is
 * `z.stringbool()` on the server, so the contract declares a string and the
 * generated client wants one. The client app's `includeSelf` carries the
 * same note for the same reason.
 */
const OWNERS_ONLY = { limit: PAGE_SIZE, owner: 'true' } as const

/**
 * The owners of one customer's organization — OPSSTD-9931.
 *
 * ## Why this is not the customer's staff list
 *
 * It was, for half a day, and that was wrong twice over. The platform
 * operator has no lever over a customer's person and MS10 removed the ones
 * that existed (OPSSTD-5814): deactivating, removing and re-roling are the
 * organization's own administrator's acts, from the user area. A list of
 * people none of whose rows the reader can act on is a list that exists only
 * to be read, and reading a customer's whole staff is exactly what the
 * operator is denied.
 *
 * **Ownership is the one thing the operator decides**, because it runs the
 * other way: an owner cannot be deactivated, removed or demoted by anybody
 * inside the organization, so it is a protection *against* those
 * administrators and an administrator able to confer it could arrange to be
 * untouchable. So this page lists the owners, and both acts on them live
 * here: conferring it, and taking it back.
 *
 * ## The first administrator is here too
 *
 * They are the organization's first owner, so they are a row like any other,
 * with the state of their invitation in the status column and the resend in
 * their row's menu. That is where the reading nothing else on the page can
 * give lives: a `ready` organization whose first message went into a spam
 * folder shows an owner who is still `invited`, with a send date a week old.
 */
function OrganizationPeoplePage() {
	const { t } = useTranslation()
	const params = Route.useParams()
	const queryClient = useQueryClient()

	const organization = useQuery(
		organizationDetailQueryOptions(params.organizationId)
	)
	const status = organization.data?.status

	const owners = useInfiniteQuery({
		...organizationPeopleQueryOptions(params.organizationId, OWNERS_ONLY),
		// Before an organization is running there is nothing to list and the
		// read would answer a refusal rather than an empty page. The notice
		// below says so in words instead.
		enabled: status !== undefined && listableOwners(status)
	})

	// The first administrator's invitation, read here so the row that belongs
	// to it can offer the resend. It is the only invitation the operator may
	// send again: `resend-first-admin-invitation` exists because nobody
	// inside a brand-new organization can press it, and every later
	// invitation has an administrator who can.
	const firstAdmin = useQuery({
		...firstAdminInvitationQueryOptions(params.organizationId),
		enabled: status !== undefined && showsFirstAdminInvitation(status)
	})

	// Not an error to show. The cursor expired or the listing moved under it,
	// and the answer is another first page.
	const cursorExpired = getErrorCode(owners.error) === 'cursor_invalid'

	async function loadNextPage() {
		const result = await infiniteQueryFetchNextPage(owners)
		if (getErrorCode(result?.error) === 'cursor_invalid') {
			await queryClient.resetQueries({
				queryKey: makeQueryKeys.organizations.people(
					params.organizationId,
					OWNERS_ONLY
				)
			})
		}
	}

	if (status !== undefined && !listableOwners(status)) {
		return (
			<Empty className='min-h-0 flex-1'>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<Hourglass />
					</EmptyMedia>
					<EmptyTitle>
						{t('operator-organizations:people.not_ready_title')}
					</EmptyTitle>
					<EmptyDescription>
						{t(
							'operator-organizations:people.not_ready_description'
						)}
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		)
	}

	const rows = owners.data?.people ?? EMPTY_PEOPLE

	return (
		<div className='flex min-h-0 min-w-0 flex-1 flex-col'>
			<div className='flex items-center gap-3 px-6 pt-4 pb-3'>
				<p className='min-w-0 flex-1 text-muted-foreground text-sm'>
					{t('operator-organizations:people.description')}
				</p>
				{status === 'ready' && (
					<AddOwnerAction organizationId={params.organizationId} />
				)}
			</div>

			<div className='flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6'>
				<UndeliveredInvitationsAlert
					pending={rows.some((person) => person.status === 'invited')}
				/>
				<OrganizationOwnersTable
					data={rows}
					firstAdminEmail={firstAdmin.data?.email ?? null}
					loadMore={{
						hasMore: owners.hasNextPage,
						loading: owners.isFetchingNextPage,
						onLoadMore: loadNextPage
					}}
					organizationId={params.organizationId}
					state={{
						empty: (
							<Empty>
								<EmptyHeader>
									<EmptyMedia variant='icon'>
										<Shield />
									</EmptyMedia>
									<EmptyTitle>
										{t(
											'operator-organizations:people.empty'
										)}
									</EmptyTitle>
								</EmptyHeader>
							</Empty>
						),
						error: owners.isError && !cursorExpired,
						loading: owners.isPending || owners.isPlaceholderData
					}}
					total={owners.data?.total ?? 0}
				/>
			</div>
		</div>
	)
}

/**
 * Whether asking for this organization's owners is a question the endpoint
 * can answer.
 *
 * An organization still climbing the provisioning ladder has no realm and no
 * membership yet, and one whose data has been destroyed has none any more.
 * Both are told in words rather than as an empty table, which would say
 * "nobody owns this" about a customer that has not been built yet.
 */
function listableOwners(status: string) {
	return status === 'ready' || status === 'suspended'
}

const EMPTY_PEOPLE: OrganizationPerson[] = []
